import { Button } from '~/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@agentselly/ui';
import { EmptyState } from '~/components/empty/EmptyState';
import { Icon } from '~/components/Icon';
import {
  cockpitActivityDesignDataConfig,
  getCockpitActivityTitle,
} from '~/components/widget/cockpit/cockpitActivityDesignDataConfig';
import { useTranslate } from '@tolgee/react';
import { formatDate } from '~/helper/formatDate';
import { formatTime } from '~/helper/formatTime';
import { formatWeekday } from '~/helper/formatWeedDate';
import { AppLink } from '~/components/AppLink';
import { clsx } from 'clsx';

interface Activity {
  readonly date: number;
  readonly type: string;
  readonly count?: number;
  readonly person?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly data?: Record<string, unknown>;
}

interface Props {
  readonly title: string;
  readonly activities: Activity[];
  readonly link: string;
  readonly infoText?: string;
}

export function CockpitActivitiesWidget(props: Props) {
  const { t } = useTranslate();

  // Group activities by date for timeline view
  const activitiesByDate = props.activities?.reduce((acc, activity) => {
    const dateStr = formatDate(new Date(activity.date));
    if (!acc[dateStr]) {
      acc[dateStr] = [];
    }
    acc[dateStr].push(activity);
    return acc;
  }, {} as Record<string, Activity[]>);

  // Get the 3 most recent dates for quick overview
  const recentDates = Object.keys(activitiesByDate || {})
    .sort((a, b) => {
      const dateA = new Date(a.split('.').reverse().join('-')).getTime();
      const dateB = new Date(b.split('.').reverse().join('-')).getTime();
      return dateB - dateA;
    })
    .slice(0, 3);

  const hasActivities = recentDates.length > 0;
  const totalActivities = props.activities?.length || 0;

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold">
            {t('cockpit_activities_widget_title', 'Aktivitäten')}
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              {totalActivities}{' '}
              {totalActivities === 1
                ? t('cockpit_activities_aktivität_singular', 'Aktivität')
                : t('cockpit_activities_aktivität_plural', 'Aktivitäten')}
            </span>
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon
                icon="listBullets"
                className="h-4 w-4 text-primary"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col pt-0">
        {hasActivities ? (
          <div className="space-y-4 flex-1">
            {recentDates.map((dateStr, dateIdx) => {
              const activities = activitiesByDate[dateStr];
              const date = new Date(dateStr.split('.').reverse().join('-'));

              // Group activities by type for this date
              const activitiesByType = activities.reduce((acc, activity) => {
                if (!acc[activity.type]) {
                  acc[activity.type] = [];
                }
                acc[activity.type].push(activity);
                return acc;
              }, {} as Record<string, Activity[]>);

              return (
                <div
                  key={dateIdx}
                  className="border-l-2 border-gray-200 dark:border-gray-700 pl-4 relative"
                >
                  {/* Date indicator */}
                  <div className="absolute -left-2 top-1 h-4 w-4 rounded-full bg-white dark:bg-gray-800 border-2 border-primary-400" />

                  <div className="mb-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {formatWeekday(date)}, {dateStr}
                    </h3>
                  </div>

                  <div className="space-y-2">
                    {Object.entries(activitiesByType).map(
                      (
                        [
                          type,
                          typeActivities,
                        ],
                        typeIdx,
                      ) => {
                        const designData =
                          cockpitActivityDesignDataConfig[type] ||
                          cockpitActivityDesignDataConfig.default;
                        const ActivityIcon = designData.icon;
                        const count = typeActivities.length;
                        const latestActivity = typeActivities.sort(
                          (a, b) => b.date - a.date,
                        )[0];

                        return (
                          <div
                            key={typeIdx}
                            className="flex items-center gap-3 py-2"
                          >
                            <div
                              className={clsx(
                                'h-6 w-6 rounded-md flex items-center justify-center',
                                type.includes('seller') ||
                                  type.includes('offer') ||
                                  type.includes('download')
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                                  : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
                              )}
                            >
                              <ActivityIcon className="h-3 w-3" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                                  {getCockpitActivityTitle(t, type)}
                                </span>
                                <div className="flex items-center gap-2">
                                  {count > 1 && (
                                    <span className="text-xs bg-gray-100 dark:bg-gray-800 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded-full font-medium">
                                      {count}×
                                    </span>
                                  )}
                                  <span className="text-xs text-muted-foreground tabular-nums">
                                    {formatTime(latestActivity.date)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <EmptyState
              icon={
                <Icon
                  icon="listBullets"
                  className="w-8 h-8 text-gray-400 dark:text-gray-500 dark:text-gray-400"
                />
              }
              title={t('cockpit_activities_empty_title', 'Aktivitäten')}
              description={t(
                'cockpit_activities_empty_description',
                'Sobald Aktivitäten auf Ihrer Immobilie stattfinden, erscheinen sie hier.',
              )}
              compact
            />
          </div>
        )}

        <AppLink
          to={props.link}
          className="mt-4"
        >
          <Button
            primary
            showArrow
            className="w-full"
          >
            {t(
              'cockpit_activities_widget_button_text',
              'Aktivitäten im Verkauf',
            )}
          </Button>
        </AppLink>
      </CardContent>
    </Card>
  );
}
